import React, { useState } from "react";
import { toast } from "sonner";
import { Save, User, Gauge } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { computeReadiness } from "@/lib/calc";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ProgressRing } from "@/components/ProgressRing";
import { SectionTitle } from "@/components/common";

const fields = [
  { key: "name", label: "Nama", placeholder: "Nama lengkap" },
  { key: "education", label: "Education", placeholder: "cth. Bachelor Student" },
  { key: "degreeTarget", label: "Degree Target", placeholder: "cth. Master" },
  { key: "field", label: "Field", placeholder: "cth. Computer Science" },
  { key: "gpa", label: "GPA / Academic Profile", placeholder: "cth. 3.62", type: "number" },
  { key: "language", label: "Language", placeholder: "cth. IELTS" },
  { key: "languageScore", label: "Language Score", placeholder: "cth. 6.5", type: "number" },
  { key: "experienceYears", label: "Lama Pengalaman (tahun)", placeholder: "cth. 1", type: "number" },
  { key: "countries", label: "Country Preference", placeholder: "cth. UK / Australia / Europe" },
  { key: "budget", label: "Budget / Financial", placeholder: "Limited / Standard / Higher Comfort" },
  { key: "targetIntake", label: "Target Intake", placeholder: "cth. 2027" },
  { key: "careerGoal", label: "Career Objective", placeholder: "cth. AI / Technology" },
];

export default function Profile() {
  const { data, updateProfile } = useApp();
  const [form, setForm] = useState({ ...data.profile });
  const readiness = computeReadiness(form, { completedTasks: data.completedTasks, testAttempts: data.testAttempts });

  const save = () => {
    updateProfile(form);
    toast.success("Profil tersimpan. Readiness diperbarui.");
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="Profil Kamu" desc="Lengkapi profil untuk matching & readiness yang lebih relevan." />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-purple-100 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.key === "experience" ? "sm:col-span-2" : ""}>
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input id={f.key} type={f.type || "text"} data-testid={`profile-${f.key}`} placeholder={f.placeholder}
                  value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1.5" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label htmlFor="experience">Experience</Label>
              <Textarea id="experience" data-testid="profile-experience" placeholder="Pengalaman kerja / organisasi / riset"
                value={form.experience || ""} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1.5" rows={2} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="achievements">Achievements</Label>
              <Textarea id="achievements" data-testid="profile-achievements" placeholder="Prestasi / penghargaan"
                value={form.achievements || ""} onChange={(e) => setForm({ ...form, achievements: e.target.value })} className="mt-1.5" rows={2} />
            </div>
          </div>
          <Button onClick={save} data-testid="profile-save" className="mt-6 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9]"><Save className="mr-1.5 h-4 w-4" /> Simpan Profil</Button>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-purple-100 bg-white p-5 text-center shadow-sm">
            <h3 className="flex items-center justify-center gap-1.5 font-display text-sm font-bold text-slate-800"><Gauge className="h-4 w-4 text-[#7C3AED]" /> Readiness Impact</h3>
            <div className="mt-4 flex justify-center"><ProgressRing value={readiness.overall} size={120} /></div>
            <p className="mt-3 text-xs text-slate-400">Confidence: {readiness.confidence}</p>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-[#FBFAFF] p-4 text-xs text-slate-500">
            Informasi sensitif tidak diminta. Data disimpan lokal di browsermu dan tidak dikirim ke server.
          </div>
        </div>
      </div>
    </div>
  );
}
